/**
 * AsyncSelectProps (P3-A polish).
 *
 * Trước: text input cho label_field + search_fields (gõ tay tên Odoo field).
 * Sau: EntityPicker `kind="odoo_field"` với parentId = model name → auto
 * load fields của model đã chọn từ Odoo introspection (cache 5 phút).
 *
 * ERP Model giữ static select để bám allowlist server-side (security).
 */
import { EntityMultiPicker } from "@/components/ui/entity-multi-picker";
import { EntityPicker } from "@/components/ui/entity-picker";

import {
  CommonProps,
  type PropsEditorProps,
  Row,
  Section,
  TextInput,
} from "./common";

// Mirror backend libs/forms/erp_binding.ALLOWED_BINDINGS. Update khi BE thêm model.
const ALLOWED_MODELS = [
  { value: "res.partner", label: "Khách hàng / NCC", default_label: "name" },
  { value: "product.template", label: "Sản phẩm (template)", default_label: "name" },
  { value: "product.product", label: "Sản phẩm (variant)", default_label: "display_name" },
  { value: "hr.department", label: "Bộ phận", default_label: "name" },
  { value: "hr.employee", label: "Nhân viên", default_label: "name" },
  { value: "account.account", label: "Tài khoản KT", default_label: "display_name" },
  { value: "uom.uom", label: "ĐVT", default_label: "name" },
  { value: "res.currency", label: "Tiền tệ", default_label: "name" },
  { value: "account.tax", label: "Thuế", default_label: "name" },
  { value: "project.project", label: "Dự án", default_label: "name" },
];

export function AsyncSelectProps(props: PropsEditorProps): JSX.Element {
  const { field, onUpdate } = props;
  const binding = field.binding ?? { erp_model: "res.partner" };

  const filterText = binding.erp_filter
    ? Object.entries(binding.erp_filter)
        .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
        .join(",")
    : "";

  const updateFilter = (raw: string) => {
    if (!raw.trim()) {
      onUpdate({ binding: { ...binding, erp_filter: null } });
      return;
    }
    const obj: Record<string, unknown> = {};
    for (const pair of raw.split(",")) {
      const [k, ...rest] = pair.split("=");
      if (!k || rest.length === 0) continue;
      const v = rest.join("=").trim();
      if (v === "true") obj[k.trim()] = true;
      else if (v === "false") obj[k.trim()] = false;
      else if (/^-?\d+(\.\d+)?$/.test(v)) obj[k.trim()] = Number(v);
      else obj[k.trim()] = v;
    }
    onUpdate({ binding: { ...binding, erp_filter: obj } });
  };

  const searchFields = binding.search_fields ?? [];

  return (
    <>
      <CommonProps {...props} />
      <Section title="ERP Binding">
        <Row label="ERP Model" hint="Chọn từ allowlist tenant.">
          <select
            value={binding.erp_model}
            onChange={(e) =>
              onUpdate({
                binding: {
                  ...binding,
                  erp_model: e.target.value,
                  label_field: null,
                  search_fields: null,
                },
              })
            }
            className="w-full h-8 rounded-md border bg-background px-2 text-sm"
          >
            {ALLOWED_MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label} ({m.value})
              </option>
            ))}
          </select>
        </Row>
        <Row
          label="Label field"
          hint="Field hiển thị trong dropdown — pick từ schema Odoo."
        >
          <EntityPicker
            kind="odoo_field"
            parentId={binding.erp_model}
            value={binding.label_field}
            onChange={(id) => onUpdate({ binding: { ...binding, label_field: id } })}
            placeholder="Mặc định: name"
            clearable
            allowFreeText
          />
        </Row>
        <Row
          label="Search fields"
          hint="Fields dùng cho ilike search — multi-select từ schema Odoo."
        >
          <EntityMultiPicker
            kind="odoo_field"
            parentId={binding.erp_model}
            value={searchFields}
            onChange={(ids) =>
              onUpdate({
                binding: { ...binding, search_fields: ids.length > 0 ? ids : null },
              })
            }
            placeholder="Trống = mặc định theo model"
          />
        </Row>
        <Row
          label="Filter cố định"
          hint="VD: active=true,customer_rank=1 — KHÔNG cho phép domain Odoo phức tạp."
        >
          <TextInput
            value={filterText}
            onChange={updateFilter}
            placeholder="key1=value1,key2=true"
          />
        </Row>
        <Row label="Order" hint='VD: "name asc"'>
          <TextInput
            value={binding.order ?? ""}
            onChange={(v) =>
              onUpdate({ binding: { ...binding, order: v || null } })
            }
          />
        </Row>
      </Section>
    </>
  );
}
